/**
 * @file 用户管理 API
 * @description 用户相关的接口请求，包括登录、用户信息、Google验证码、用户增删改查等
 * @module services/api/User
 */
import { get, post } from './request';

// ==================== 身份认证相关 ====================

/**
 * 用户登录
 * @description 用户名密码登录；已绑定 Google 验证码的账号必须带上 googleCode
 * @param {Object} params - 登录参数（对应后端 SysLoginDTO）
 * @param {string} params.username - 用户名
 * @param {string} params.password - 密码
 * @param {number} [params.googleCode] - Google 验证码（6位数字，int32）
 * @param {any} [options] - 额外的请求配置
 * @returns {Promise<RequestResult<SysLoginRespVO>>} 登录结果（token / needBind 等）
 */
export async function login(
  params: {
    username?: string;
    password?: string;
    googleCode?: number;
  },
  options?: any,
) {
  return post<SysLoginRespVO>('/auth/login', params, options);
}

/**
 * 用户登出
 * @description 用户登出
 * @param {Object} params - 登出参数
 * @returns {Promise<RequestResult>} 登出结果
 */
export async function logoutAuth(params: any) {
  return post('/auth/logout', params);
}

/**
 * 修改当前登录用户密码
 * @param {Object} params - 修改密码参数
 * @param {string} params.oldPassword - 原密码
 * @param {string} params.newPassword - 新密码
 * @returns {Promise<RequestResult>} 修改结果
 */
export async function updatePassword(params: {
  oldPassword: string;
  newPassword: string;
}) {
  return post('/auth/updatePassword', params);
}

/**
 * 修改当前登录用户密码（新）
 * @param {Object} params - 修改密码参数
 * @param {string} params.oldPassword - 原密码
 * @param {string} params.newPassword - 新密码
 * @param {string} params.confirmPassword - 确认新密码
 * @param {number} [params.googleCode] - 6 位动态口令，后端校验
 * @returns {Promise<RequestResult>} 修改结果
 */
export async function changePassword(params: {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
  googleCode?: number;
}) {
  return post('/sys/users/change-password', params);
}

/**
 * 获取当前用户信息
 * @description 获取当前登录用户的详细信息，包括菜单权限
 * @returns {Promise<RequestResult<MenuItem[]>>} 用户菜单树
 */
export async function getUserMsg() {
  return post<MenuItem[]>('/sys/menus/getCurrUserMenuTree', {});
}

// ==================== Google 验证码相关 ====================
// 注意：本节为历史实现，部分字段与后端 OpenAPI 定义不一致（见下方各函数注释）。
// 新代码请使用 @/services/api/googleAuth 里按接口文档写的版本。

/**
 * 校验 Google 动态口令
 * @description 验证用户输入的 Google Authenticator 动态口令是否正确
 * @param {Object} params - 校验参数
 * @param {string} params.googleAuthCode - 6位动态口令
 * @returns {Promise<RequestResult>} 校验结果
 */
export async function checkGoogleCode({ googleAuthCode }: { googleAuthCode: string }) {
  return post('/google/auth/verify', { code: googleAuthCode });
}

/**
 * 绑定 Google 验证码
 * @description 为用户绑定 Google Authenticator
 * @param {BindGoogleCodeParams} params - 绑定参数
 * @returns {Promise<RequestResult<string>>} 绑定结果
 * @deprecated 入参与后端 GoogleAuthBindDTO（{ username, code }）不一致，
 *             userId/googleCode/googleAuthCode 都不会被后端读取，
 *             请改用 googleAuthBindSelf() / googleAuthBindUser()
 */
export async function bindGoogleCode(params: BindGoogleCodeParams) {
  return post<string>('/google/auth/bind', params);
}

/**
 * 获取用户 Google 验证码信息
 * @description 获取用户的 Google Authenticator 二维码和密钥
 * @param {Object} params - 查询参数
 * @param {string} params.username - 用户名
 * @returns {Promise<RequestResult<{codeUrl: string; googleCode: string}>>} 二维码URL和密钥
 * @deprecated 后端 GoogleAuthQRCodeVO 返回的是 qrCodeUrl / secret，
 *             这里声明的 codeUrl / googleCode 取不到值，请改用 googleAuthInfo()
 */
export async function getUserGoogleCode(params: { username: string }) {
  const query = new URLSearchParams({ username: params.username }).toString();
  return get<{ codeUrl: string; googleCode: string }>(
    `/google/auth/info?${query}`,
    {},
  );
}

/**
 * 删除用户 Google 验证码绑定
 * @description 解除用户的 Google Authenticator 绑定
 * @param {Object} params - 删除参数
 * @param {string} params.googleCode - Google 验证码
 * @param {string} params.loginName - 登录名
 * @param {number} params.userId - 用户ID
 * @returns {Promise<RequestResult<string>>} 删除结果
 */
export async function deleteGoogleCode(params: {
  googleCode: string;
  loginName: string;
  userId: number;
}) {
  return post<string>('/google/auth/unbind', params);
}

// ==================== 角色查询 ====================

/**
 * 获取角色列表
 * @description 查询角色列表，用于用户分配角色时的下拉选择
 * @param {string} [roleName] - 角色名称（可选，模糊搜索）
 * @returns {Promise<RequestResult<RoleListItem[]>>} 角色列表
 */
export async function getRoles(roleName?: string) {
  return post<RoleListItem[]>('/sys/roles/options', { roleName });
}

// ==================== 用户增删改查 ====================

/**
 * 新增用户
 * @description 创建新用户并分配角色
 * @param {AddUserParams} params - 用户新增参数
 * @returns {Promise<RequestResult<string>>} 新增结果
 */
export async function addUserUrl(params: AddUserParams) {
  return post<string>('/sys/users/create', params);
}

/**
 * 编辑用户
 * @description 修改用户信息（密码、角色等）
 * @param {EditUserParams} params - 用户编辑参数
 * @returns {Promise<RequestResult<string>>} 编辑结果
 */
export async function editUser(params: EditUserParams) {
  return post<string>('/sys/users/update', params);
}

/**
 * 删除用户
 * @description 根据用户ID删除用户
 * @param {Object} params - 删除参数
 * @param {number} params.id - 用户ID
 * @param {number} [params.googleCode] - 6 位动态口令，后端校验
 * @returns {Promise<RequestResult<string>>} 删除结果
 */
export async function onDeleteUser(params: {
  id: number;
  googleCode?: number;
}) {
  return post<string>('/sys/users/delete', params);
}

/**
 * 获取用户管理列表
 * @description 分页查询用户列表
 * @param {number} pageNum - 当前页码
 * @param {number} pageSize - 每页条数
 * @param {Object} params - 查询参数
 * @param {string} params.loginName - 登录名（模糊搜索）
 * @returns {Promise<RequestResult<UserManagementListItem[]>>} 用户列表
 */
export async function getUserMangementList(params:{
  pageNum: number,
  pageSize: number,
  roleName: string,
  userName: string,
}) {
  return post<UserManagementListItem[]>('/sys/users/list', params);
}

// ==================== 用户额度管理 ====================

/**
 * 获取用户额度
 * @description 查询用户的额度限制配置
 * @param {UserManagementListItem} params - 用户信息
 * @returns {Promise<RequestResult<UserQuotaLimits>>} 用户额度信息
 */
export async function getQuota(params: UserManagementListItem) {
  return post<UserQuotaLimits>('/system/userquota/findByUserQuota', params);
}

/**
 * 设置用户额度
 * @description 修改用户的额度限制配置
 * @param {UserQuotaLimits} params - 额度配置参数
 * @returns {Promise<RequestResult<string>>} 设置结果
 */
export async function setUserQuota(params: UserQuotaLimits) {
  return post<string>('/system/userquota/edit', params);
}


/**
 * 更新 IP 白名单
 * @description 修改 IP 白名单配置
 * @param {IpWhitelistParams} params - IP 白名单配置参数
 * @returns {Promise<RequestResult<string>>} 更新结果
 */
export async function updateIpWhitelist(params: {
    id?: number,
    ip: string,
    status: number,
    remark: string,
    /** 6 位动态口令，后端校验 */
    googleCode?: number
}) {
  return post<string>('/ipAccessList/update', params);
}


/**
 * 获取 IP 白名单列表
 * @description 查询 IP 白名单列表
 * @param {Object} params - 查询参数
 * @param {number} params.pageNum - 当前页码
 * @param {number} params.pageSize - 每页条数
 * @returns {Promise<RequestResult<IpWhitelistListItem[]>>} IP 白名单列表
 */
export async function getIpWhitelistList(params: {
  pageSize: number,
  pageNum: number,
  ip: string,
  status: number
}) {
  return post<any>('/ipAccessList/list', params);
}


/**
 * 删除 IP 白名单
 * @description 根据 IP 白名单ID删除 IP 白名单
 * @param {Object} params - 删除参数
 * @param {number} params.id - IP 白名单ID
 * @returns {Promise<RequestResult<string>>} 删除结果
 */
export async function deleteIpWhitelist(params: {
  id: number;
  /** 6 位动态口令，后端校验 */
  googleCode?: number;
}) {
  return post<any>('/ipAccessList/delete', params);
}


/**
 * 新增 IP 白名单
 * @description 创建新 IP 白名单配置
 * @param {IpWhitelistParams} params - IP 白名单配置参数
 * @returns {Promise<RequestResult<string>>} 新增结果
 */
export async function addIpWhitelist(params: {
    ip: string,
    status: number,
    remark: string,
    /** 6 位动态口令，后端校验 */
    googleCode?: number
}) {
  return post<any>('/ipAccessList/add', params);
}
